import React, { useEffect, useMemo, useState } from 'react';
import { Brain, Shield, Target, Zap, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';

type FileUploadRow = Tables<'file_uploads'>;

interface ProcessingMetadata {
  confidence_score?: number;
  averageConfidence?: number;
  automation_rate?: number;
  automationRate?: number;
  vat_compliance?: number;
  vatCompliance?: number;
  error_count?: number;
}

interface AiProcessingAnalysisProps {
  organizationId: string;
  mandateId: string;
  refreshKey?: number;
}

interface AIMetrics {
  confidenceScore: number;
  processedTransactions: number;
  automationRate: number;
  vatCompliance: number;
}

const INITIAL_METRICS: AIMetrics = {
  confidenceScore: 0,
  processedTransactions: 0,
  automationRate: 0,
  vatCompliance: 0,
};

const formatMetadata = (metadata: FileUploadRow['processing_metadata']): ProcessingMetadata => {
  if (!metadata || typeof metadata !== 'object') return {};
  return metadata as ProcessingMetadata;
};

const isSameDay = (dateA: Date, dateB: Date) =>
  dateA.getFullYear() === dateB.getFullYear() &&
  dateA.getMonth() === dateB.getMonth() &&
  dateA.getDate() === dateB.getDate();

export const AiProcessingAnalysis: React.FC<AiProcessingAnalysisProps> = ({ organizationId, mandateId, refreshKey = 0 }) => {
  const [metrics, setMetrics] = useState<AIMetrics>(INITIAL_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasData, setHasData] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!organizationId || !mandateId) {
      setMetrics(INITIAL_METRICS);
      return;
    }

    const loadMetrics = async () => {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('file_uploads')
        .select('processing_metadata, transactions_count, processed_at, created_at')
        .eq('organization_id', organizationId)
        .eq('mandate_id', mandateId)
        .eq('processing_status', 'completed')
        .order('processed_at', { ascending: false })
        .limit(25);

      if (error) {
        setError(error.message);
        setMetrics(INITIAL_METRICS);
        setHasData(false);
        setLoading(false);
        return;
      }

      const uploads = data ?? [];
      
      if (uploads.length === 0) {
        setMetrics(INITIAL_METRICS);
        setHasData(false);
        setLoading(false);
        return;
      }

      setHasData(true);
      const latestUpload = uploads.find((row) => row.processing_metadata) || uploads[0];
      const latestMeta = formatMetadata(latestUpload?.processing_metadata ?? null);

      const confidenceScore = Number(latestMeta.confidence_score ?? latestMeta.averageConfidence ?? 0);
      const automationRate = latestMeta.automation_rate ?? latestMeta.automationRate ?? null;

      const today = new Date();
      const processedToday = uploads
        .filter((row) => row.processed_at && isSameDay(new Date(row.processed_at), today))
        .reduce((sum, row) => sum + (row.transactions_count ?? 0), 0);

      const processedFallback = latestUpload?.transactions_count ?? 0;

      const vatCompliance = latestMeta.vat_compliance ?? latestMeta.vatCompliance;

      setMetrics({
        confidenceScore: Number.isFinite(confidenceScore) ? confidenceScore : 0,
        processedTransactions: processedToday > 0 ? processedToday : processedFallback,
        automationRate: automationRate != null && Number.isFinite(Number(automationRate)) ? Number(automationRate) : 0,
        vatCompliance: vatCompliance != null && Number.isFinite(Number(vatCompliance)) ? Number(vatCompliance) : 100,
      });

      setLoading(false);
    };

    void loadMetrics();
  }, [organizationId, mandateId, refreshKey]);

  const complianceChecks = useMemo(() => ([
    { name: "Swiss 2025 VAT Rates (8.1%)", status: "Pass", icon: "fa-check-circle" },
    { name: "CAMT.053 Format Valid", status: "Pass", icon: "fa-check-circle" },
    { name: "FINMA AI Compliance", status: "Pass", icon: "fa-check-circle" },
    { name: "10-Year Audit Trail", status: "Active", icon: "fa-clock" }
  ]), []);

  const displayMetrics = metrics || INITIAL_METRICS;
  const confidence = displayMetrics.confidenceScore || 0;
  const automation = displayMetrics.automationRate || 0;
  const processed = displayMetrics.processedTransactions || 0;
  const vatCompliance = displayMetrics.vatCompliance || 0;

  const handleRefresh = async () => {
    if (!organizationId || !mandateId) return;

    setLoading(true);
    const { data, error } = await supabase
      .from('file_uploads')
      .select('processing_metadata, transactions_count, processed_at, created_at')
      .eq('organization_id', organizationId)
      .eq('mandate_id', mandateId)
      .eq('processing_status', 'completed')
      .order('processed_at', { ascending: false })
      .limit(25);

    if (error) {
      toast({
        title: 'Refresh Failed',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    const uploads = data ?? [];
    
    if (uploads.length === 0) {
      setMetrics(INITIAL_METRICS);
      setHasData(false);
      setLoading(false);
      toast({
        title: 'No Data',
        description: 'No completed uploads yet',
      });
      return;
    }

    setHasData(true);
    const latestUpload = uploads.find((row) => row.processing_metadata) || uploads[0];
    const latestMeta = formatMetadata(latestUpload?.processing_metadata ?? null);

    const confidenceScore = Number(latestMeta.confidence_score ?? latestMeta.averageConfidence ?? 0);
    const automationRate = latestMeta.automation_rate ?? latestMeta.automationRate ?? null;

    const today = new Date();
    const processedToday = uploads
      .filter((row) => row.processed_at && isSameDay(new Date(row.processed_at), today))
      .reduce((sum, row) => sum + (row.transactions_count ?? 0), 0);

    const processedFallback = latestUpload?.transactions_count ?? 0;

    const vatCompliance = latestMeta.vat_compliance ?? latestMeta.vatCompliance;

    setMetrics({
      confidenceScore: Number.isFinite(confidenceScore) ? confidenceScore : 0,
      processedTransactions: processedToday > 0 ? processedToday : processedFallback,
      automationRate: automationRate != null && Number.isFinite(Number(automationRate)) ? Number(automationRate) : 0,
      vatCompliance: vatCompliance != null && Number.isFinite(Number(vatCompliance)) ? Number(vatCompliance) : 100,
    });

    setLoading(false);
    toast({
      title: 'Metrics Updated',
      description: 'Latest AI processing data loaded',
    });
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-brand-primary flex items-center gap-2">
          <Brain className="h-5 w-5" />
          Swiss AI Processing Analysis
        </h3>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="p-2 hover:bg-brand-secondary rounded-lg transition-colors disabled:opacity-50"
          title="Refresh metrics"
        >
          <RefreshCw className={`h-4 w-4 text-brand-primary ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Failed to load latest metrics: {error}
        </div>
      )}

      {loading && (
        <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
          Updating metrics…
        </div>
      )}

      {!loading && !hasData && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
          No completed uploads yet. Upload a file to see AI processing metrics.
        </div>
      )}

      {/* Main Confidence Score */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-6 rounded-xl flex flex-col items-center justify-center text-center mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-5 w-5" />
          <p className="text-sm opacity-90">Claude Sonnet 4.5 Confidence</p>
        </div>
        <p className="text-4xl font-space-grotesk font-bold my-2">
          {confidence.toFixed(1)}%
        </p>
        <div className="w-full bg-white/20 rounded-full h-2.5 mb-2">
          <div
            className="bg-green-400 h-2.5 rounded-full transition-all duration-1000"
            style={{ width: `${Math.min(confidence, 100)}%` }}
          ></div>
        </div>
        <p className="text-xs opacity-80">
          {confidence >= 90 ? 'Auto-approved' : 'Requires review'}
        </p>
      </div>

      {/* Real-time Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">Processed</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{processed}</p>
          <p className="text-xs text-green-600">transactions processed</p>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Target className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Automation</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{automation.toFixed(1)}%</p>
          <p className="text-xs text-blue-600">auto-classified</p>
        </div>
      </div>

      {/* Swiss Compliance Checks */}
      <div className="space-y-3">
        <h4 className="font-bold text-brand-text-primary text-sm flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Swiss Finance OS Compliance
        </h4>
        {complianceChecks.map((check, index) => (
          <div key={index} className="flex items-center justify-between text-sm py-2">
            <span className="text-brand-text-secondary">{check.name}</span>
            <span className={`flex items-center gap-2 font-medium ${
              check.status === 'Pass' ? 'text-green-600' :
              check.status === 'Active' ? 'text-blue-600' : 'text-orange-600'
            }`}>
              <i className={`fa-solid ${check.icon}`}></i> {check.status}
            </span>
          </div>
        ))}
      </div>

      {/* Swiss VAT 2025 Update Notice */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <i className="fa-solid fa-info-circle text-blue-600"></i>
          <h4 className="font-bold text-blue-800 text-sm">2025 Swiss VAT Update</h4>
        </div>
        <p className="text-xs text-blue-700 mb-3">
          Standard rate increased to 8.1% (from 7.7%). AI automatically applying new rates.
        </p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-white p-2 rounded border">
            <span className="font-semibold text-blue-800">8.1%</span>
            <br />
            <span className="text-blue-600">Standard</span>
          </div>
          <div className="bg-white p-2 rounded border">
            <span className="font-semibold text-blue-800">2.6%</span>
            <br />
            <span className="text-blue-600">Reduced</span>
          </div>
          <div className="bg-white p-2 rounded border">
            <span className="font-semibold text-blue-800">3.8%</span>
            <br />
            <span className="text-blue-600">Hotels</span>
          </div>
        </div>
      </div>

      {/* Performance Badge */}
      <div className="mt-4 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-green-100 to-blue-100 text-green-800 text-xs font-medium">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          Swiss AI • Claude Sonnet 4.5 • 50 req/min
          <Brain className="h-3 w-3" />
        </span>
      </div>
    </div>
  );
};